import styled from '@emotion/styled';
import { Card, Button } from 'antd';
import type { ComponentType } from 'react';
import { useNavigate } from 'react-router-dom';

interface VibrantModuleCardProps {
    icon: ComponentType<{ className?: string; style?: React.CSSProperties }>;
    title: string;
    description: string;
    color: string;
    path: string;
}

const GlassCard = styled(Card)`
    position: relative;
    overflow: hidden;
    transition: all 0.2s ease;
    border: 1px solid hsl(0 0% 80% / 0.4); /* same as border-border/40 */
    background: hsl(0 0% 100% / 0.7);
    backdrop-filter: blur(8px);
    border-radius: 8px; /* optional — add rounding if you want */

    &:hover {
        border-color: #44338a; /* purple-500/60 equivalent */
        transform: translateY(-2px);
        box-shadow: 0 8px 20px -6px hsl(270 100% 60% / 0.25);
    }

    /* Target inner card body if you want spacing / radius */
    .ant-card-body {
        border-radius: inherit;
        transition: inherit;
        /* Stretch the body to full card height so the inner flex column can push
           the "Explore" button to the bottom, aligning it across all cards. */
        height: 100%;
        display: flex;
        flex-direction: column;
    }
`;

export const VibrantModuleCard = ({ icon: Icon, title, description, color, path }: VibrantModuleCardProps) => {
    const navigate = useNavigate();
    return (
        <GlassCard className="h-full">
            <div className="relative flex flex-col items-center text-center space-y-4 h-full min-h-[240px]">
                <div
                    className="flex h-12 w-12 items-center justify-center rounded-xl transition-all duration-200"
                    style={{
                        backgroundColor: `hsl(${color} / 0.12)`,
                    }}
                >
                    <Icon className="text-2xl" style={{ color: `hsl(${color})` }} />
                </div>
                <div className="space-y-2 flex-1 flex flex-col">
                    <h3 className="font-semibold text-base leading-tight text-foreground">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed flex-1">{description}</p>
                </div>
                <button
                    className="mt-auto border border-purpleFont hover:bg-purpleFont hover:text-white transition-all py-1.5 px-4 rounded-xl"
                    onClick={() => path && navigate(path)}
                >
                    Explore
                </button>
            </div>
        </GlassCard>
    );
};
